import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell, LabelList,
} from 'recharts';

// Shared presentational card for a Polymarket multi-outcome distribution:
// top-7 current probabilities as horizontal bars + top-5 YES-price history.
// Used by both the year-end Fed-rate market and the annual rate-cut market so
// the two read identically.

// Top-5 buckets get a stable color used in BOTH the bar chart and the history
// lines, so a bucket reads the same in both.
const COLORS = ['#60a5fa', '#34d399', '#f59e0b', '#f87171', '#a78bfa'];
const GRAY = '#4b5563';
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const TIMEFRAMES = [
  { key: '1M', days: 31 },
  { key: '3M', days: 93 },
  { key: 'ALL', days: null },
];

function monthLabel(iso) {
  const m = parseInt((iso || '').slice(5, 7), 10);
  return Number.isFinite(m) && m >= 1 && m <= 12 ? MONTHS[m - 1] : iso;
}

// First date of each distinct YYYY-MM in the (already date-sorted) series.
function monthTicks(rows) {
  const ticks = [];
  const seen = new Set();
  for (const r of rows) {
    const ym = (r.date || '').slice(0, 7);
    if (ym && !seen.has(ym)) {
      seen.add(ym);
      ticks.push(r.date);
    }
  }
  return ticks;
}

function PolymarketDistributionCard({
  data,
  title,
  subtitle,
  unavailableTitle,
  historyHeading = 'History · top 5 buckets',
  historyCaption = 'Polymarket YES prices · bars = top 7 buckets now · lines = top 5 history',
  yAxisWidth = 52,
}) {
  const [timeframe, setTimeframe] = useState('ALL');

  const labels = useMemo(() => data?.history_labels || [], [data]);
  const colorFor = useMemo(() => {
    const map = {};
    labels.forEach((l, i) => { map[l] = COLORS[i % COLORS.length]; });
    return map;
  }, [labels]);

  const barData = useMemo(
    () => (data?.bars || []).map((b) => ({
      label: b.label,
      pct: Number((b.probability_pct ?? 0).toFixed(2)),
    })),
    [data],
  );

  const history = useMemo(() => {
    const rows = data?.history || [];
    const tf = TIMEFRAMES.find((t) => t.key === timeframe);
    if (!tf?.days || !rows.length) return rows;
    const last = new Date(`${rows[rows.length - 1].date}T00:00:00Z`);
    last.setUTCDate(last.getUTCDate() - tf.days);
    const cutoff = last.toISOString().slice(0, 10);
    return rows.filter((r) => r.date >= cutoff);
  }, [data, timeframe]);

  if (!data) {
    return (
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-gray-300">{unavailableTitle}</h4>
        <p className="text-xs text-gray-500 mt-1">Polymarket event unavailable.</p>
      </div>
    );
  }

  const ticks = monthTicks(history);

  return (
    <div className="bg-gray-900 border border-gray-700 rounded-lg p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-300">{data.title || title}</h4>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        {data.polymarket_url && (
          <a
            href={data.polymarket_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-300 hover:text-blue-200"
          >
            View on Polymarket ↗
          </a>
        )}
      </div>

      {/* Top-7 current probabilities */}
      <div className="min-h-[240px]">
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={barData} layout="vertical" margin={{ top: 5, right: 44, left: 16, bottom: 5 }}>
            <XAxis
              type="number"
              domain={[0, 'auto']}
              tick={{ fill: '#9ca3af', fontSize: 10 }}
              tickFormatter={(v) => `${v}%`}
            />
            <YAxis type="category" dataKey="label" tick={{ fill: '#d1d5db', fontSize: 11 }} width={yAxisWidth} />
            <Tooltip
              contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
              labelStyle={{ color: '#9ca3af' }}
              formatter={(v) => [`${v}%`, 'Probability']}
              cursor={{ fill: 'rgba(96, 165, 250, 0.1)' }}
            />
            <Bar dataKey="pct" radius={[0, 4, 4, 0]}>
              {barData.map((d) => (
                <Cell key={d.label} fill={colorFor[d.label] || GRAY} />
              ))}
              <LabelList dataKey="pct" position="right" fill="#d1d5db" fontSize={10} formatter={(v) => `${v}%`} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Historical probabilities for the top 5 buckets */}
      <div className="mt-4 border-t border-gray-800 pt-3">
        <div className="flex items-center justify-between mb-1">
          <h5 className="text-sm font-semibold text-gray-200">{historyHeading}</h5>
          <div className="inline-flex rounded-md border border-gray-700 overflow-hidden">
            {TIMEFRAMES.map((t) => (
              <button
                key={t.key}
                onClick={() => setTimeframe(t.key)}
                className={`px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                  t.key === timeframe ? 'bg-blue-600 text-white' : 'bg-gray-900 text-gray-400 hover:bg-gray-800'
                }`}
              >
                {t.key}
              </button>
            ))}
          </div>
        </div>
        {history.length < 2 ? (
          <p className="text-xs text-gray-500">No history available for this range.</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={history} margin={{ top: 5, right: 12, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis
                dataKey="date"
                ticks={ticks}
                tickFormatter={monthLabel}
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                stroke="#374151"
              />
              <YAxis
                domain={[0, 'auto']}
                tick={{ fill: '#9ca3af', fontSize: 11 }}
                stroke="#374151"
                tickFormatter={(v) => `${v}%`}
              />
              <Tooltip
                contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
                labelStyle={{ color: '#9ca3af' }}
                formatter={(v, name) => [v == null ? '—' : `${v}%`, name]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {labels.map((label) => (
                <Line
                  key={label}
                  type="monotone"
                  dataKey={label}
                  name={label}
                  stroke={colorFor[label]}
                  dot={false}
                  strokeWidth={2}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
        <p className="text-[10px] text-gray-500 mt-1">{historyCaption}</p>
      </div>
    </div>
  );
}

export default PolymarketDistributionCard;
